import React, { useState, useEffect } from 'react';
import { XCircle, CheckCircle, PauseCircle, Smartphone, User, Crown, Users } from 'lucide-react';
import { useFlashMessage } from '../components/FlashMessage';
import ConfirmDialog from '../components/ConfirmDialog';
import API from '../services/api';
import { FormSelect } from '../components/FormSelect';

const AdminDevices = () => {
  const { showSuccess, showDanger, FlashContainer } = useFlashMessage();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    filter: 'all', // all, active, inactive
    device_type: 'all', // all, ios, android
    search: ''
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 20
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pagination.currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: pagination.currentPage,
        limit: pagination.limit,
        filter: filters.filter,
        device_type: filters.device_type,
        search: filters.search
      });

      const response = await API.get(`/admin/devices?${queryParams}`);

      if (response.data.success) {
        // Handle both array and single object responses
        let devicesData = response.data.data;
        if (devicesData && !Array.isArray(devicesData)) {
          // If it's a single object, wrap it in an array
          devicesData = [devicesData];
        } else if (!devicesData) {
          devicesData = [];
        }

        setDevices(devicesData);
        setStats(response.data.stats);
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            ...response.data.pagination
          }));
        }
      }

    } catch (error) {
      showDanger('Veriler yüklenirken hata: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteLoading(true);
    try {
      await API.delete(`/admin/devices/${deviceToDelete.id}`);
      showSuccess('Cihaz başarıyla devre dışı bırakıldı');
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
      loadData();
    } catch (error) {
      showDanger('İşlem başarısız: ' + error.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 inline-flex items-center gap-1">
        <CheckCircle className="h-3 w-3" /> Aktif
      </span>
    ) : (
      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 inline-flex items-center gap-1">
        <PauseCircle className="h-3 w-3" /> Devre Dışı
      </span>
    );
  };

  const getDeviceTypeBadge = (deviceType) => {
    return deviceType === 'ios' ? (
      <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 inline-flex items-center gap-1">
        <Smartphone className="h-3 w-3" /> iOS
      </span>
    ) : (
      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 inline-flex items-center gap-1">
        <Smartphone className="h-3 w-3" /> Android
      </span>
    );
  };

  const getUserTypeBadge = (userType) => {
    const userTypeConfig = {
      system_admin: {
        color: 'bg-red-50 text-[#7a2c2c]',
        icon: <Crown className="h-3 w-3" />,
        text: 'Sistem Admin'
      },
      owner: {
        color: 'bg-blue-100 text-blue-800',
        icon: <User className="h-3 w-3" />,
        text: 'İşletme Sahibi'
      },
      staff: {
        color: 'bg-green-100 text-green-800',
        icon: <Users className="h-3 w-3" />,
        text: 'Personel'
      }
    };

    const config = userTypeConfig[userType] || userTypeConfig.staff;
    return (
      <span className={`px-2 py-1 text-xs rounded-full inline-flex items-center gap-1 ${config.color}`}>
        {config.icon} {config.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7a2c2c]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6">
      <FlashContainer />

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Kayıtlı Cihazlar</h1>
        <p className="text-gray-600 mt-1">Mobil uygulama cihaz kayıtlarını görüntüleyin</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3 lg:gap-4">
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="flex justify-center mb-1 lg:mb-2">
                <Smartphone className="h-8 w-8 lg:h-10 lg:w-10 text-gray-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Toplam</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="flex justify-center mb-1 lg:mb-2">
                <CheckCircle className="h-8 w-8 lg:h-10 lg:w-10 text-green-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-green-600">{stats.active || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Aktif</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="flex justify-center mb-1 lg:mb-2">
                <PauseCircle className="h-8 w-8 lg:h-10 lg:w-10 text-gray-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-gray-600">{stats.inactive || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Devre Dışı</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="flex justify-center mb-1 lg:mb-2">
                <Smartphone className="h-8 w-8 lg:h-10 lg:w-10 text-gray-900" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.ios_count || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">iOS</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body text-center py-3 lg:py-4">
              <div className="flex justify-center mb-1 lg:mb-2">
                <Smartphone className="h-8 w-8 lg:h-10 lg:w-10 text-green-600" />
              </div>
              <p className="text-lg lg:text-2xl font-bold text-green-600">{stats.android_count || 0}</p>
              <p className="text-xs lg:text-sm text-gray-600">Android</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
              <FormSelect
                name="filter"
                value={filters.filter}
                onChange={(e) => handleFilterChange('filter', e.target.value)}
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'active', label: 'Aktif' },
                  { value: 'inactive', label: 'Devre Dışı' }
                ]}
              />
            </div>

            {/* Device Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cihaz Tipi</label>
              <FormSelect
                name="device_type"
                value={filters.device_type}
                onChange={(e) => handleFilterChange('device_type', e.target.value)}
                options={[
                  { value: 'all', label: 'Tümü' },
                  { value: 'ios', label: 'iOS' },
                  { value: 'android', label: 'Android' }
                ]}
              />
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ara</label>
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Kullanıcı, cihaz adı, OneSignal ID..."
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-[#7a2c2c] focus:ring-1 focus:ring-[#7a2c2c]"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Devices List */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">
            Cihazlar ({pagination.totalCount || 0})
          </h3>
        </div>
        <div className="card-body p-0">
          {devices && devices.length > 0 ? (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kullanıcı
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tip
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Cihaz
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lokasyonlar
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Son Aktif
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {devices.map(device => (
                      <tr key={device.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{device.full_name || '-'}</div>
                            <div className="text-sm text-gray-500">@{device.username}</div>
                            <div className="mt-1">{getUserTypeBadge(device.user_type)}</div>
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getDeviceTypeBadge(device.device_type)}
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">{device.device_name || 'Bilinmiyor'}</div>
                            <div className="text-xs text-gray-500 mt-1">v{device.app_version || '?'}</div>
                            <div className="text-xs text-gray-400 mt-1 font-mono truncate max-w-xs" title={device.sms_ist_id}>
                              {device.sms_ist_id ? device.sms_ist_id.substring(0, 20) + '...' : '-'}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900 max-w-xs">
                            {device.user_locations || '-'}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {getStatusBadge(device.is_active)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(device.last_active_at || device.created_at)}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {device.is_active && (
                            <button
                              onClick={() => handleDeleteClick(device)}
                              className="text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                              title="Devre Dışı Bırak"
                            >
                              <XCircle className="h-4 w-4" />
                              Devre Dışı Bırak
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="lg:hidden space-y-3 p-4">
                {devices.map(device => (
                  <div key={device.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                    {/* Header */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {getDeviceTypeBadge(device.device_type)}
                      {getStatusBadge(device.is_active)}
                      {getUserTypeBadge(device.user_type)}
                    </div>

                    {/* User Info */}
                    <div className="mb-3">
                      <div className="font-medium text-gray-900">{device.full_name || 'İsimsiz'}</div>
                      <div className="text-sm text-gray-600">@{device.username}</div>
                    </div>

                    {/* Device Info */}
                    <div className="grid grid-cols-2 gap-2 mb-3 text-xs">
                      <div className="bg-gray-50 rounded p-2">
                        <div className="text-gray-600">Cihaz</div>
                        <div className="font-medium text-gray-900">{device.device_name || 'Bilinmiyor'}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2">
                        <div className="text-gray-600">Versiyon</div>
                        <div className="font-medium text-gray-900">v{device.app_version || '?'}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2 col-span-2">
                        <div className="text-gray-600">Lokasyonlar</div>
                        <div className="font-medium text-gray-900">{device.user_locations || '-'}</div>
                      </div>
                      <div className="bg-gray-50 rounded p-2 col-span-2">
                        <div className="text-gray-600">Son Aktif</div>
                        <div className="font-medium text-gray-900">
                          {formatDate(device.last_active_at || device.created_at)}
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded p-2 col-span-2">
                        <div className="text-gray-600">SMS IST ID</div>
                        <div className="font-medium text-gray-900 font-mono text-xs truncate">
                          {device.sms_ist_id || '-'}
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    {device.is_active && (
                      <button
                        onClick={() => handleDeleteClick(device)}
                        className="w-full py-2 px-3 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 inline-flex items-center justify-center gap-1"
                      >
                        <XCircle className="h-4 w-4" />
                        Devre Dışı Bırak
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-4 py-3 border-t border-gray-200 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 flex justify-between sm:hidden">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                        disabled={!pagination.hasPrevPage}
                        className="btn-secondary disabled:opacity-50"
                      >
                        Önceki
                      </button>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                        disabled={!pagination.hasNextPage}
                        className="btn-secondary disabled:opacity-50"
                      >
                        Sonraki
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm text-gray-700">
                          <span className="font-medium">{pagination.totalCount}</span> cihazdan{' '}
                          <span className="font-medium">{(pagination.currentPage - 1) * pagination.limit + 1}</span>
                          {' '}-{' '}
                          <span className="font-medium">
                            {Math.min(pagination.currentPage * pagination.limit, pagination.totalCount)}
                          </span>{' '}
                          arası gösteriliyor
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                          <button
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                            disabled={!pagination.hasPrevPage}
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            ‹
                          </button>
                          <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                            {pagination.currentPage} / {pagination.totalPages}
                          </span>
                          <button
                            onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                            disabled={!pagination.hasNextPage}
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                          >
                            ›
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="flex justify-center mb-4">
                <Smartphone className="h-16 w-16 text-gray-400" />
              </div>
              <p className="text-gray-500">Kayıtlı cihaz bulunamadı</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Cihazı Devre Dışı Bırak"
        message={`"${deviceToDelete?.full_name}" kullanıcısının "${deviceToDelete?.device_name}" cihazını devre dışı bırakmak istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Devre Dışı Bırak"
        cancelText="İptal"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default AdminDevices;
